import { db } from "@/firebase";
import {
  doc,
  collection,
  onSnapshot,
  setDoc,
  deleteDoc,
} from "firebase/firestore";
import { colorForOwner } from "@/components/alice/normalize";

// How often a viewer refreshes its "I'm here" heartbeat.
const PRESENCE_HEARTBEAT_MS = 5000;
// A heartbeat older than this is treated as offline, so a crashed/closed tab
// drops off on its own even if the cleanup-on-leave write never lands.
const PRESENCE_STALE_MS = 15000;
// Local clock tick so staleness re-evaluates even when no snapshot arrives.
const PRESENCE_CLOCK_TICK_MS = 3000;

// Live presence for the project Chat page (ProjectChat.vue). While the page is
// mounted, each viewer writes a per-user heartbeat doc and subscribes to the
// whole presence collection; the header renders everyone else who's currently
// here. This mirrors the typing-lock heartbeat pattern in communalChat.js but
// is mode-independent — it tracks anyone viewing the Chat tab, personal or team.
//
// Per-user docs (not a shared map) avoid Firestore write contention: every
// viewer heartbeats concurrently, so each client only ever writes its own row.
export default {
  data() {
    return {
      presenceUsers: [],
      presenceUnsub: null,
      presenceHeartbeatTimer: null,
      presenceClockTimer: null,
      presenceNow: 0,
      // Stable reference so we can remove the same listener on teardown.
      presenceUnloadHandler: null,
    };
  },
  computed: {
    // Everyone else (not me) whose heartbeat is still fresh.
    onlineUsers() {
      const me = this.user && this.user.uid;
      return (this.presenceUsers || [])
        .filter((u) => {
          if (!u.userUid || u.userUid === me) return false;
          return (
            !!u.lastSeen && this.presenceNow - u.lastSeen < PRESENCE_STALE_MS
          );
        })
        .map((u) => ({
          ...u,
          color: colorForOwner(u.userName || ""),
        }));
    },
    onlineCount() {
      return this.onlineUsers.length;
    },
    // First names for the tooltip / label, e.g. "Sven, Lotte".
    onlineNames() {
      return this.onlineUsers
        .map((u) => (u.userName || "Someone").split(/[\s@]/)[0])
        .join(", ");
    },
  },
  watch: {
    // Auth may resolve after this page mounts — start broadcasting once we know
    // who the viewer is (idempotent: startPresence tears down any prior run).
    "user.uid"(uid) {
      if (uid && !this.presenceHeartbeatTimer) this.startPresence();
    },
  },
  mounted() {
    this.startPresence();
  },
  beforeUnmount() {
    this.stopPresence();
  },
  methods: {
    presenceProjectUid() {
      return (this.project && this.project.uid) || this.$route.params.id;
    },
    presenceCollectionRef() {
      return collection(db, "projects", this.presenceProjectUid(), "presence");
    },
    presenceDocRef() {
      return doc(
        db,
        "projects",
        this.presenceProjectUid(),
        "presence",
        this.user.uid
      );
    },

    startPresence() {
      const uid = this.user && this.user.uid;
      if (!uid || !this.presenceProjectUid()) return;
      this.stopPresence();

      this.presenceNow = Date.now();
      this.writePresence();

      this.presenceHeartbeatTimer = setInterval(
        () => this.writePresence(),
        PRESENCE_HEARTBEAT_MS
      );
      this.presenceClockTimer = setInterval(() => {
        this.presenceNow = Date.now();
      }, PRESENCE_CLOCK_TICK_MS);

      this.presenceUnsub = onSnapshot(this.presenceCollectionRef(), (snap) => {
        this.presenceUsers = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      });

      // Best-effort removal if the tab is closed without a clean unmount.
      this.presenceUnloadHandler = () => {
        deleteDoc(this.presenceDocRef()).catch(() => {});
      };
      window.addEventListener("beforeunload", this.presenceUnloadHandler);
    },

    writePresence() {
      const uid = this.user && this.user.uid;
      if (!uid) return;
      setDoc(
        this.presenceDocRef(),
        {
          userUid: uid,
          userName: this.userName || null,
          userPhoto: this.userPhoto || null,
          lastSeen: Date.now(),
        },
        { merge: true }
      ).catch((err) => console.error("presence heartbeat failed:", err));
    },

    stopPresence() {
      if (this.presenceHeartbeatTimer) {
        clearInterval(this.presenceHeartbeatTimer);
        this.presenceHeartbeatTimer = null;
      }
      if (this.presenceClockTimer) {
        clearInterval(this.presenceClockTimer);
        this.presenceClockTimer = null;
      }
      if (this.presenceUnsub) {
        this.presenceUnsub();
        this.presenceUnsub = null;
      }
      if (this.presenceUnloadHandler) {
        window.removeEventListener("beforeunload", this.presenceUnloadHandler);
        this.presenceUnloadHandler = null;
      }
      // Drop my row immediately so others see me leave without waiting for stale.
      if (this.user && this.user.uid && this.presenceProjectUid()) {
        deleteDoc(this.presenceDocRef()).catch(() => {});
      }
    },
  },
};
